import { useState, useEffect } from 'react';
import { X, Search, FileText, CheckCircle2, Printer, Loader2, Trash2 } from 'lucide-react';
import { ventasService } from '@/services/ventas.service';
import { useAuthStore } from '@/stores/auth-store';
import { dialogs } from '@/stores/dialog-store';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { SaleTicket } from '@/components/SaleTicket';

interface LoadSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoad: (saleId: string, items: any[], customer: any) => void;
}

export function LoadSaleModal({ isOpen, onClose, onLoad }: LoadSaleModalProps) {
  const { user } = useAuthStore();
  const [sales, setSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  
  const printRef = useRef<HTMLDivElement>(null);
  const [saleToPrint, setSaleToPrint] = useState<any>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => setSaleToPrint(null),
  });

  useEffect(() => {
    if (saleToPrint) {
      setTimeout(() => {
        handlePrint();
        setPrintingId(null);
      }, 200);
    }
  }, [saleToPrint, handlePrint]);

  useEffect(() => {
    if (isOpen) {
      loadSales();
    }
  }, [isOpen]);

  const loadSales = async () => {
    if (!user?.company?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      // Load both QUOTATION and PENDING_PAYMENT
      const res1 = await ventasService.findAll({ companyId: user.company.id, status: 'QUOTATION', limit: 50 });
      const res2 = await ventasService.findAll({ companyId: user.company.id, status: 'PENDING_PAYMENT', limit: 50 });
      
      const all = [...(res1.data || []), ...(res2.data || [])];
      all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setSales(all);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = sales.filter(s => 
    s.id.includes(searchTerm) || 
    (s.customer?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = async (sale: any) => {
    try {
      const fullSale = await ventasService.findById(sale.id);
      
      const mappedItems = fullSale.items.map((item: any) => ({
        productId: item.productId,
        name: item.product?.name || 'Producto Desconocido',
        sku: item.product?.sku || '',
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        pricePublic: Number(item.product?.pricePublic || item.unitPrice),
        priceWholesale: item.product?.priceWholesale ? Number(item.product.priceWholesale) : undefined,
        wholesaleMinQuantity: item.product?.wholesaleMinQuantity,
        discount: Number(item.discount || 0),
        subtotal: Number(item.quantity) * Number(item.unitPrice),
        stock: item.product?.stock || 0,
      }));

      onLoad(sale.id, mappedItems, fullSale.customer);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePrintSale = async (saleId: string) => {
    try {
      setPrintingId(saleId);
      const fullSale = await ventasService.findById(saleId);
      setSaleToPrint(fullSale);
    } catch (err) {
      console.error(err);
      setPrintingId(null);
    }
  };

  const handleCancel = async (sale: any) => {
    const isAdmin = ['SUPER_ADMIN', 'ADMIN', 'GERENTE'].includes(user?.role || '');
    const isPending = sale.status === 'PENDING_PAYMENT';

    // Only admins can cancel PENDING sales
    if (isPending && !isAdmin) {
      await dialogs.alert(
        'Solo el Administrador puede cancelar ventas pendientes de pago.',
        'Sin permiso'
      );
      return;
    }

    const confirmed = await dialogs.confirm(
      `¿Estás seguro de que deseas cancelar esta ${isPending ? 'venta pendiente' : 'cotización'}? Esta acción no se puede deshacer.`,
      { title: 'Cancelar documento', confirmText: 'Sí, Cancelar', cancelText: 'No' }
    );
    if (!confirmed) return;

    try {
      setCancellingId(sale.id);
      await ventasService.cancel(sale.id, {
        cancelledById: user!.id,
        reason: isPending ? 'Cancelado por administrador desde caja' : 'Cotización cancelada',
      });
      loadSales(); // Refresh list
    } catch (err: any) {
      await dialogs.alert(err?.response?.data?.message || 'Error al cancelar', 'Error');
    } finally {
      setCancellingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
          <h2 className="text-xl font-black text-gray-800 flex items-center gap-2">
            <FileText className="text-[#007AFF]" />
            CARGAR COTIZACIÓN O PENDIENTE
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input 
              type="text"
              placeholder="Buscar por cliente o folio..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:border-[#007AFF] outline-none transition-colors"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No hay cotizaciones o ventas pendientes.</div>
          ) : (
            <div className="flex flex-col gap-2">
              {filtered.map(sale => (
                <div 
                  key={sale.id}
                  onClick={() => handleSelect(sale)}
                  className="p-4 rounded-xl border border-gray-100 hover:border-[#007AFF] hover:bg-blue-50/30 cursor-pointer transition-colors flex items-center justify-between"
                >
                  <div>
                    <div className="font-bold text-gray-800">
                      {sale.customer ? sale.customer.name : 'Público en General'}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 flex gap-3">
                      <span>{new Date(sale.createdAt).toLocaleString()}</span>
                      <span className="font-mono bg-gray-100 px-1 rounded">{sale.id.slice(-6).toUpperCase()}</span>
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                        sale.status === 'QUOTATION' ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {sale.status === 'QUOTATION' ? 'COTIZACIÓN' : 'PENDIENTE'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-black text-lg text-gray-800 mr-2">
                      ${Number(sale.grandTotal).toFixed(2)}
                    </div>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handlePrintSale(sale.id); }}
                      disabled={printingId === sale.id}
                      className="bg-gray-100 text-gray-600 p-2 rounded-lg hover:bg-gray-200 hover:text-gray-900 transition-colors disabled:opacity-50"
                      title="Reimprimir"
                    >
                      {printingId === sale.id ? <Loader2 size={20} className="animate-spin" /> : <Printer size={20} />}
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleCancel(sale); }}
                      disabled={cancellingId === sale.id}
                      className="bg-red-50 text-red-500 p-2 rounded-lg hover:bg-red-100 hover:text-red-700 transition-colors disabled:opacity-50"
                      title={sale.status === 'QUOTATION' ? 'Cancelar cotización' : 'Cancelar pendiente (solo admin)'}
                    >
                      {cancellingId === sale.id ? <Loader2 size={20} className="animate-spin" /> : <Trash2 size={20} />}
                    </button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleSelect(sale); }}
                      className="bg-[#007AFF] text-white p-2 rounded-lg hover:bg-blue-600 transition-colors"
                      title="Cargar a caja"
                    >
                      <CheckCircle2 size={20} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="hidden">
        <SaleTicket ref={printRef} sale={saleToPrint} />
      </div>
    </div>
  );
}
